'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { 
  Search, 
  ArrowRight, 
  CheckCircle,
  FileSearch,
  Users,
  BarChart,
  Microscope
} from 'lucide-react';

export default function EtudesRecherche() {
  const t = useTranslations('expertisePages');
  
  const services = [
    {
      icon: FileSearch,
      title: t('etudes.services.baseline.title'),
      description: t('etudes.services.baseline.description'),
    },
    {
      icon: Users,
      title: t('etudes.services.socioeconomic.title'),
      description: t('etudes.services.socioeconomic.description'),
    },
    {
      icon: BarChart,
      title: t('etudes.services.market.title'),
      description: t('etudes.services.market.description'),
    },
    {
      icon: Microscope,
      title: t('etudes.services.actionResearch.title'),
      description: t('etudes.services.actionResearch.description'),
    },
  ];

  const methodology = t.raw('etudes.methodology') as string[];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-36 pb-24 bg-gradient-to-br from-cerad-green via-cerad-green-dark to-cerad-blue relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 hero-pattern" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
          >
            <div>
              <Link 
                href="/expertises" 
                className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
              >
                {t('backToExpertises')}
              </Link>
              <h1 className="font-montserrat text-4xl md:text-5xl font-bold text-white mb-6">
                {t('etudes.title')}
              </h1>
              <p className="text-white/90 text-lg mb-8">
                {t('etudes.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/contact" className="btn-gold inline-flex items-center justify-center gap-2">
                  {t('requestQuote')}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="w-80 h-80 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Search className="w-40 h-40 text-white" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('etudes.servicesTitle').split(' ').slice(0, -2).join(' ')} <span className="text-cerad-green">{t('etudes.servicesTitle').split(' ').slice(-2).join(' ')}</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 rounded-3xl p-8 lg:p-10 card-hover"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-cerad-green to-cerad-green-light rounded-2xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-montserrat text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('ourApproach').split(' ')[0]} <span className="text-cerad-green">{t('ourApproach').split(' ').slice(1).join(' ')}</span>
              </h2>
              <div className="space-y-4">
                {methodology.map((item: string) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-cerad-gold flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cerad-gold to-cerad-gold-light rounded-3xl p-10 text-white"
            >
              <h3 className="font-montserrat text-2xl font-bold mb-4">
                {t('toolsTechnologies')}
              </h3>
              <p className="text-white/90">
                {t('etudes.toolsDescription')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('etudes.ctaTitle')}
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              {t('etudes.ctaDescription')}
            </p>
            <Link href="/contact" className="btn-primary inline-flex items-center gap-2">
              {t('requestFreeQuote')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
